import { WisdomCard } from '@components/Cards';
import Loader from '@components/Loader';
import NapsterSpinner from '@components/NapsterSpinner';
import Search from '@components/Search';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useFavorites } from '@hooks/useFavorites';
import useWisdomInfiniteScroll from '@hooks/useWisdomInfiniteScroll';
import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { placeHolderData } from '../../config';
import { Album } from '../../types';
import { getTrackUrlForLocale } from '../../utils/localeUtils';

export const WisdomPage = memo(() => {
  const { t } = useTranslation();
  const { setActivePopup } = useCommonState();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { toggleFavorite } = useFavorites();
  const { loadAlbumById } = useAlbumData();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // State for genre filtering and search
  const [selectedGenre, setSelectedGenre] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);

  // Use shared infinite scroll hook - load when audiobooks page is active
  const {
    albums: wisdomAlbums,
    loading: wisdomLoading,
    hasMore,
    error,
    loadMore,
    reset,
    sentinelRef,
  } = useWisdomInfiniteScroll({
    rootMargin: '200px', // Load more when 200px from bottom
    enabled: true, // Only infinite scroll when this page is active
    scrollContainerRef, // Pass the scroll container reference
    genre: selectedGenre === 'All' ? undefined : selectedGenre,
    search: searchQuery.trim() || undefined,
  });

  const playAlbum = useCallback(
    async (album: Album) => {
      try {
        setLoadingAlbumId(album.id);
        // Fetch complete album data from API
        const albumData = await loadAlbumById(album.id);

        if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
          console.warn(
            'No tracks found in album:',
            albumData?.albumName || album.id,
          );
          setLoadingAlbumId(null);
          return;
        }

        // Get the first contributor for artist info
        const contributor =
          albumData.contributors && albumData.contributors.length > 0
            ? albumData.contributors[0]
            : null;
        const firstTrack = albumData.tracks[0];

        const playlist = {
          currentIndex: 0,
          album: albumData,
        };

        // Add first track to recent tracks when album is played
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);

        // Store current page as previous page before opening music player
        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
        setLoadingAlbumId(null);
      } catch (error) {
        console.error('Error loading album:', error);
        setLoadingAlbumId(null);
      }
    },
    [
      loadAlbumById,
      setActivePlaylist,
      setActivePopup,
      addRecentTrack,
      preferredLocale,
    ],
  );

  return (
    <div className="flex flex-col h-full pt-0 sm:pt-[10px]">
      {/* Fixed Header */}
      <div className="flex-shrink-0">
        <h1 className="text-[32px] sm:text-[40px] px-[24px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
          {t('audiobooksPage.title')}
        </h1>
        <div className="text-[14px] px-[24px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
          {t('audiobooksPage.subtitle')}
        </div>
        {/* Search and Genre Filter */}
        <Search
          selectedGenre={selectedGenre}
          onGenreChange={setSelectedGenre}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          category="book"
        />
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[220px]">
          {/* Loading State - only show when page is active and no albums and loading */}
          {wisdomLoading && wisdomAlbums.length === 0 && (
            <div className="responsive-grid gap-4">
              {[...Array(8)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="flex flex-col animate-pulse">
                  <div className="w-full aspect-square rounded-[8px] bg-gray-700 mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          )}

          {/* Error State - only show when there's an error and no albums */}
          {error && wisdomAlbums.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="text-red-400 mb-4">
                  <svg
                    className="w-8 h-8 mx-auto mb-2"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-medium">
                    {t('audiobooksPage.failedToLoad')}
                  </p>
                  <p className="text-xs text-red-300 mt-1">{error}</p>
                </div>
                <button
                  onClick={() => reset()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200">
                  {t('common.tryAgain')}
                </button>
              </div>
            </div>
          )}

          {/* Empty State - show when no albums and not loading and no error */}
          {!wisdomLoading && !error && wisdomAlbums.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center text-white/60">
                <div className="mb-4">
                  <svg
                    className="w-12 h-12 mx-auto mb-4 text-white/40"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <p className="text-lg font-medium mb-2">
                  {selectedGenre !== 'All' && searchQuery
                    ? t('audiobooksPage.noContentGenreSearch', { genre: selectedGenre, search: searchQuery })
                    : selectedGenre !== 'All'
                    ? t('audiobooksPage.noContentGenre', { genre: selectedGenre })
                    : searchQuery
                    ? t('audiobooksPage.noContentSearch', { search: searchQuery })
                    : t('audiobooksPage.noContentDefault')}
                </p>
                <p className="text-sm text-white/40">
                  {t('audiobooksPage.noContentHelp')}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {wisdomAlbums.length > 0 && (
            <div className="responsive-grid gap-6">
              {wisdomAlbums?.map((album: Album, index: number) => {
                const contributor = album.contributors[0];
                const isLoading = loadingAlbumId === album.id;
                return (
                  <div
                    key={`audiobooks-album-${
                      album.id || album.albumName || index
                    }`}
                    className="flex flex-col relative">
                    <WisdomCard
                      album={album}
                      contributor={contributor}
                      albumArtUrl={album.albumArtUrl}
                      albumName={album.albumName}
                      likes={album.likes}
                      isLiked={album.isLiked}
                      onClick={() => playAlbum(album)}
                      onFavoriteClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(
                          'album',
                          album.id,
                          album.isLiked,
                          album.albumName,
                          contributor?.givenName,
                          album.category,
                        );
                      }}
                      showLikesCount={true}
                    />
                    {isLoading && (
                      <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <NapsterSpinner />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Infinite scroll sentinel */}
              {hasMore && wisdomAlbums.length > 0 && (
                <div
                  ref={sentinelRef}
                  className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 flex justify-center py-8">
                  {wisdomLoading ? (
                    <Loader size="medium" text={t('audiobooksPage.loadingMore')} />
                  ) : (
                    <div className="w-full h-4"></div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error message for loading more items */}
          {error && wisdomAlbums.length > 0 && (
            <div className="flex justify-center mt-4">
              <div className="text-red-400 text-center">
                <p className="text-sm">{t('audiobooksPage.failedToLoadMore')}</p>
                <button
                  onClick={() => loadMore()}
                  className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors duration-200">
                  {t('common.tryAgain')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

WisdomPage.displayName = 'WisdomPage';
